import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { downloadBlob } from 'src/app/shared/utilities/download-blob';
import { DocAdminNSwagMigrationService } from '../../../compliance/api/migration/doc-admin-nswag-migration.service';
import { DocumentTypeSummaryBase } from '../../../shared/lib/compliance-shared/models/document-type-summary-base.model';
import { PagingTokenFixed } from '../../../shared/models/core';
import { DocumentTypeActions } from '../actions';
import * as fromSelectors from '../doc-admin.selector';

@Injectable()
export class DocumentTypeEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _docAdminService: DocAdminNSwagMigrationService,
        private readonly _store: Store,
        private readonly _router: Router
    ) {}

    loadDocuments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                DocumentTypeActions.DocumentTypes.getDocumentTypes,
                DocumentTypeActions.DocumentTypes.setDocumentTypesFilter
            ),
            concatLatestFrom(() => this._store.select(fromSelectors.selectFilter)),
            switchMap(([action, filters]) =>
                this._docAdminService.getDocumentTypes(filters).pipe(
                    map((documents: PagingTokenFixed<DocumentTypeSummaryBase[]>) => DocumentTypeActions.DocumentTypes.getDocumentTypesSuccess({ documents })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getDocumentTypesFailure({ error })))
                )
            )
        )
    );

    downloadcDocuments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.downloadDocumentTypes),
            switchMap((action) =>
                this._docAdminService.downloadDocumentBlobById(action.documentId).pipe(
                    map((blob) => {
                        downloadBlob(blob, action.fileName);
                        return DocumentTypeActions.DocumentTypes.downloadDocumentTypesSuccess({
                            blob,
                            fileName: action.fileName
                        });
                    }),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.downloadDocumentTypesFailure({ error })))
                )
            )
        )
    );

    loadDocumentsTypes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.getDocumentTypesList),
            switchMap(() =>
                this._docAdminService.getDocumentTypesList().pipe(
                    map((documentTypesList) =>
                        DocumentTypeActions.DocumentTypes.getDocumentTypesListSuccess({ documentTypesList })
                    ),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getDocumentTypesListFailure({ error })))
                )
            )
        )
    );

    loadDocumentsTypesByName$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.getDocumentTypesListByName),
            switchMap((action) =>
                this._docAdminService.getDocumentTypesList(action.name).pipe(
                    map((documentTypesList) =>
                        DocumentTypeActions.DocumentTypes.getDocumentTypesListSuccess({ documentTypesList })
                    ),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getDocumentTypesListFailure({ error })))
                )
            )
        )
    );

    loadDocumentsTypesById$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                DocumentTypeActions.DocumentTypes.getDocumentType,
                DocumentTypeActions.DocumentTypes.addSubTypeSuccess,
                DocumentTypeActions.DocumentTypes.addDocCodeSuccess,
                DocumentTypeActions.DocumentTypes.editDocCodeSuccess,
                DocumentTypeActions.DocumentTypes.deleteDocCodeSuccess
            ),
            switchMap((action) =>
                this._docAdminService.getDocumentType(action.id).pipe(
                    map((documentType) => DocumentTypeActions.DocumentTypes.getDocumentTypeSuccess({ documentType })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getDocumentTypeFailure({ error })))
                )
            )
        )
    );

    createDocumentType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.createDocumentType),
            switchMap((action) =>
                this._docAdminService.createDocumentType(action.documentType).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.goToDocumentPage({ documentId: document.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.createDocumentTypeFailure({ error })))
                )
            )
        )
    );

    updateDocumentType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.updateDocumentType),
            switchMap((action) =>
                this._docAdminService.updateDocumentType(action.documentType, action.documentId).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.goToDocumentPage({ documentId: document.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.createDocumentTypeFailure({ error })))
                )
            )
        )
    );

    addSubType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.addSubType),
            switchMap((action) =>
                this._docAdminService.addSubType(action.name, action.id).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.addSubTypeSuccess({ id: action.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.addSubTypeFailure({ error })))
                )
            )
        )
    );

    addDocCode$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.addDocCode),
            switchMap((action) =>
                this._docAdminService.addDocCode(action.newDocCode, action.id).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.addDocCodeSuccess({ id: action.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.addDocCodeFailure({ error })))
                )
            )
        )
    );

    editDocCode$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.editDocCode),
            switchMap((action) =>
                this._docAdminService.editDocCode(action.newDocCode, action.id, action.codeId).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.editDocCodeSuccess({ id: action.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.editDocCodeFailure({ error })))
                )
            )
        )
    );

    goToDocumentPage$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.goToDocumentPage),
                map((action) => {
                    this._router.navigate([`/compliance/per-diem-doc-admin/${action.documentId}`]);
                })
            ),
        { dispatch: false }
    );

    uploadSupplementalFile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.uploadSupplementalFile),
            switchMap((action) =>
                this._docAdminService.uploadSupplementalFile(action.file).pipe(
                    map((file) => DocumentTypeActions.DocumentTypes.uploadSupplementalFileSuccess({ file })),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypes.uploadSupplementalFileFailure({ error }))
                    )
                )
            )
        )
    );

    deleteDocCode$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.deleteDocCode),
            switchMap((action) =>
                this._docAdminService.deleteDocCode(action.id, action.codeId).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.deleteDocCodeSuccess({ id: action.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.deleteDocCodeFailure({ error })))
                )
            )
        )
    );

    showUploadSuccessMessage$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.uploadSupplementalFileSuccess),
                map(() => {
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'File upload success'
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    showUploadFailureMessage$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.uploadSupplementalFileFailure),
                map(() => {
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'File upload failure'
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    createSubtypeFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    DocumentTypeActions.DocumentTypes.addSubTypeFailure,
                    DocumentTypeActions.DocumentTypes.addDocCodeFailure
                ),
                map(() => {
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'Add Action Failure'
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    deleteDocCodeFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.deleteDocCodeFailure),
                map(() => {
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'Delete Doc Code Failure'
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    displayToaster$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    DocumentTypeActions.DocumentTypes.createDocumentTypeFailure,
                    DocumentTypeActions.DocumentTypes.editDocCodeFailure
                ),
                map((action) =>
                    this._store.dispatch(
                        showToasterMessage({ message: 'Error: ' + action.error.error.error.message, duration: 3000 })
                    )
                )
            ),
        { dispatch: false }
    );

    displayToasterSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.goToDocumentPage),
                map((action) => this._store.dispatch(showToasterMessage({ message: 'Document type created success' })))
            ),
        { dispatch: false }
    );
}
